import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import { AlertTriangle, X } from 'lucide-react';
import { Drawer, DrawerContent, DrawerDescription, DrawerTitle } from 'components/ui/drawer';

interface AuditDrawerFrameProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  children: ReactNode;
}

/** Shared 560px drawer chrome (frame, header, error boundary) for `HistoryDrawer` and `EventDrawer`
 * (C1) — mounted only while `open`, or vaul's exit animation never resolves under jsdom. */
const AuditDrawerFrame = ({ open, onOpenChange, title, description, children }: AuditDrawerFrameProps) => {
  const { t } = useTranslation();
  if (!open) return null;

  return (
    // `autoFocus`: vaul blocks Radix's open auto-focus by default, leaving focus behind the modal (CA16).
    <Drawer open autoFocus direction="right" shouldScaleBackground={false} onOpenChange={onOpenChange}>
      <DrawerContent side="right" className="w-[560px] max-w-full" overlayClassName="bg-zinc-950/[.18]">
        <div className="flex shrink-0 items-start justify-between gap-3 border-b px-5 py-4">
          <div className="min-w-0">
            <DrawerTitle className="truncate text-[15.5px] font-semibold leading-tight">{title}</DrawerTitle>
            <DrawerDescription className="mt-1 truncate text-[12.5px]">{description}</DrawerDescription>
          </div>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            aria-label={t('Close')}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            <X className="h-[17px] w-[17px]" aria-hidden="true" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          <Sentry.ErrorBoundary
            fallback={
              <div role="alert" className="flex flex-col items-center gap-2 px-4 py-10 text-center">
                <AlertTriangle className="h-8 w-8 text-amber-500" aria-hidden="true" />
                <p className="text-[13.5px] font-medium">{t('Unable to load the history')}</p>
              </div>
            }
          >
            {children}
          </Sentry.ErrorBoundary>
        </div>
      </DrawerContent>
    </Drawer>
  );
};

export default AuditDrawerFrame;
